// User-facing display names + metadata for the creative patterns of each
// director agent.
//
// The director_payload returns `selected_pattern_key` (e.g. "scolding_mom"),
// which is the technical key used by the agent metadata. The actual display
// names live in `metadata.video_studio.creative_patterns[].display_name` of
// each agent in agent-config-front, but the frontend doesn't have access to
// that metadata directly. We mirror them here.
//
// PATTERNS_BY_STYLE is the structured version, used by the PreviewApproval
// dropdown to show only the patterns that apply to the current draft's
// videoType. Without this filter, a sassy-object draft would show animated
// patterns in the dropdown and the agent would ignore them on regenerate.
//
// PATTERN_DISPLAY_NAMES is a flat lookup derived from PATTERNS_BY_STYLE,
// used by code paths that don't know the styleId.

export interface PatternMeta {
  /** Internal key used by the agent metadata + the LLM output. */
  key: string;
  /** User-facing display name in the dropdown. */
  name: string;
  /** Short description shown next to the name. */
  description: string;
}

export const PATTERNS_BY_STYLE: Record<string, PatternMeta[]> = {
  // ── video_director_sassy_v1 (the product talks with attitude) ──
  "sassy-object": [
    {
      key: "scolding_mom",
      name: "Mamá regañona",
      description: "El producto te reta como si fueras un nene de 8 años.",
    },
    {
      key: "deadpan_passive_aggressive",
      name: "Pasivo-agresivo monótono",
      description: "Suelta verdades incómodas con cero emoción, como un colega harto.",
    },
    {
      key: "existential_breakdown",
      name: "Crisis existencial dramática",
      description: "El producto entra en crisis filosófica y melodrama por tu maltrato.",
    },
    {
      key: "smug_superiority",
      name: "Superioridad insufrible",
      description: "Se sabe superior a cualquier alternativa y te lo restriega.",
    },
    {
      key: "exhausted_employee",
      name: "Empleado exhausto",
      description: "El monólogo del que trabaja 24/7 sin descanso ni propina.",
    },
  ],

  // ── video_director_animated_v1 (the problem is the character) ──
  "animated-problem": [
    {
      key: "suffering_victim",
      name: "Víctima sufriente",
      description: "El problema sufre, suplica y te pide salvación.",
    },
    {
      key: "smug_villain",
      name: "Villano que disfruta",
      description: "El problema es un villano sádico que celebra hacerte daño.",
    },
    {
      key: "tired_employee",
      name: "Empleado cansado",
      description: "El problema lleva años trabajando y quiere renunciar.",
    },
    {
      key: "negotiating_problem",
      name: "Problema que negocia",
      description: "El problema te propone llevarse bien para quedarse.",
    },
    {
      key: "horror_buildup",
      name: "Amenaza creciente",
      description: "El problema acecha en silencio y revela una amenaza inminente.",
    },
  ],
};

/**
 * Flat lookup of all display names indexed by key. Used by code paths that
 * don't know the styleId.
 */
export const PATTERN_DISPLAY_NAMES: Record<string, string> = Object.values(
  PATTERNS_BY_STYLE
).reduce(
  (acc, patterns) => {
    patterns.forEach((p) => {
      acc[p.key] = p.name;
    });
    return acc;
  },
  {} as Record<string, string>
);
